import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { inputClass, labelClass, parseWeight } from './api';

const PCT = { 1: 100, 2: 95, 3: 93, 4: 90, 5: 87, 6: 85, 7: 83, 8: 80, 9: 77, 10: 75 };
const MOVEMENTS = ['Pull-Up', 'Dip', 'Muscle-Up'];
const round05 = (n) => Math.round(n / 0.5) * 0.5;
const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function FreeCalculator({ publicMode = false }) {
  const [movement, setMovement] = useState('Pull-Up');
  const [bw, setBw] = useState('');
  const [load, setLoad] = useState('');
  const [reps, setReps] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const calculate = (e) => {
    e.preventDefault();
    const bwN = parseWeight(bw);
    const loadN = parseWeight(load);
    const repsN = parseInt(reps, 10);
    if (bw === '' || isNaN(bwN) || bwN <= 0) {
      setError('Enter your bodyweight to continue.');
      setResult(null);
      return;
    }
    if (load === '' || isNaN(loadN) || loadN < 0) {
      setError('Enter the added weight you lifted — use 0 for bodyweight only.');
      setResult(null);
      return;
    }
    if (reps === '' || isNaN(repsN) || repsN < 1 || repsN > 10) {
      setError('Enter reps between 1 and 10 for an accurate estimate.');
      setResult(null);
      return;
    }
    setError('');
    const testedTotal = bwN + loadN;
    const oneRmTotal = testedTotal / (PCT[repsN] / 100);
    const ladder = [];
    for (let r = 1; r <= 10; r++) {
      const totalAtR = oneRmTotal * (PCT[r] / 100);
      ladder.push({
        reps: r,
        added: round05(Math.max(0, totalAtR - bwN)),
        total: round05(totalAtR),
        tested: r === repsN,
      });
    }
    setResult({
      bw: bwN,
      oneRmAdded: round05(Math.max(0, oneRmTotal - bwN)),
      oneRmTotal: round05(oneRmTotal),
      ladder,
    });
  };

  return (
    <div className="max-w-2xl" data-testid="free-calculator-page">
      <span className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.25em]">
        Coach Rands Method
      </span>
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none mt-3 mb-3">
        Strength Calculator
      </h1>
      <p className="text-mono-mute font-body mb-8 leading-relaxed">
        Enter one honest set and get your estimated max, plus the full ladder down to 10 reps.
      </p>

      <div className="inline-flex border border-mono-line divide-x divide-mono-line mb-8" data-testid="calc-movement-toggle">
        {MOVEMENTS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMovement(m)}
            className={`px-5 py-2.5 font-heading text-sm font-semibold uppercase tracking-wider transition-colors ${
              movement === m ? 'bg-white text-black' : 'text-mono-mute hover:text-white'
            }`}
            data-testid={`calc-movement-${m.toLowerCase().replace(/-/g, '')}`}
          >
            {m}
          </button>
        ))}
      </div>

      <form onSubmit={calculate} className="space-y-5" data-testid="calc-form">
        <div>
          <label className={labelClass}>Bodyweight (kg)</label>
          <input
            type="text" inputMode="text" value={bw} onChange={(e) => setBw(e.target.value)}
            placeholder="e.g. 73" className={inputClass} data-testid="calc-bodyweight-input"
          />
        </div>
        <div>
          <label className={labelClass}>Added weight lifted (kg — 0 if bodyweight only)</label>
          <input
            type="text" inputMode="text" value={load} onChange={(e) => setLoad(e.target.value)}
            placeholder="e.g. 15" className={inputClass} data-testid="calc-load-input"
          />
        </div>
        <div>
          <label className={labelClass}>Reps performed (1–10, all clean)</label>
          <input
            type="number" value={reps} onChange={(e) => setReps(e.target.value)}
            placeholder="e.g. 5" className={inputClass} data-testid="calc-reps-input"
          />
        </div>

        {error && <p className="text-red-400 text-sm font-body" data-testid="calc-error">{error}</p>}

        <button
          type="submit"
          className="w-full sm:w-auto bg-white hover:bg-[#E5E5E5] text-black font-heading font-bold text-lg uppercase tracking-wider rounded-none px-10 py-3.5 transition-all active:scale-95"
          data-testid="calc-submit-btn"
        >
          Calculate
        </button>
      </form>

      {result && (
        <div className="mt-10 space-y-8" data-testid="calc-results">
          <div className="border border-crimson bg-mono-card p-6 md:p-8" data-testid="calc-headline-card">
            <p className="text-mono-mute font-heading text-xs font-bold uppercase tracking-[0.2em] mb-2" data-testid="calc-headline-label">
              Estimated 1-rep max — {movement}
            </p>
            <p className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none">
              <span className="text-crimson" data-testid="calc-onerm-added">+{fmt(result.oneRmAdded)}</span>
              <span className="text-white"> kg belt</span>
            </p>
            <p className="text-mono-mute font-body text-sm mt-3" data-testid="calc-onerm-total">
              {fmt(result.oneRmTotal)}kg total weight at bodyweight {fmt(result.bw)}kg
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white mb-4">Your ladder</h2>
            <div className="overflow-x-auto border border-mono-line">
              <table className="w-full text-left font-body text-sm" data-testid="calc-ladder-table">
                <thead>
                  <tr className="border-b border-mono-line bg-mono-surface">
                    {['Reps', 'Added weight', 'Total weight'].map((h) => (
                      <th key={h} className="px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-mono-mute whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.ladder.map((row) => (
                    <tr
                      key={row.reps}
                      className={`border-b border-mono-line/60 transition-colors ${
                        row.tested ? 'bg-crimson/10' : 'hover:bg-mono-card'
                      }`}
                      data-testid={row.tested ? 'calc-ladder-row-tested' : 'calc-ladder-row'}
                    >
                      <td className="px-4 py-3 text-white whitespace-nowrap">
                        {row.reps} {row.reps === 1 ? 'rep' : 'reps'}
                        {row.tested && (
                          <span className="ml-2 border border-crimson text-crimson font-heading text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5" data-testid="calc-tested-tag">
                            Tested
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap ${row.tested ? 'text-crimson font-semibold' : 'text-white'}`}>
                        +{fmt(row.added)}kg
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap ${row.tested ? 'text-crimson font-semibold' : 'text-white'}`}>
                        {fmt(row.total)}kg
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-mono-mute/80 font-body text-xs leading-relaxed" data-testid="calc-note">
            Estimated using a standard %1-rep-max table, the same relationship used across strength and conditioning
            coaching. Reps performed is always exactly 100% of your max, so your tested set reproduces exactly in the
            ladder and the headline figure always matches the top row. Total weight = bodyweight + added weight. Numbers
            rounded to the nearest 0.5kg for practical use. Accuracy is best close to your tested rep count and
            drifts further out at the extremes — treat the far end of the ladder as a guide, not a guarantee.
          </p>

          {publicMode && (
            <div className="border-t border-mono-line pt-6" data-testid="calc-soft-capture">
              <p className="text-white font-body mb-4" data-testid="calc-soft-capture-line">
                {movement === 'Pull-Up'
                  ? 'Want to track this over 12 weeks instead of just once?'
                  : 'The free tracker covers weighted pull-ups. Want to track those over 12 weeks?'}
              </p>
              <Link
                to="/free"
                className="inline-flex items-center gap-1.5 border border-crimson text-crimson hover:bg-crimson hover:text-white font-heading font-bold text-sm uppercase tracking-wider rounded-none px-5 py-2.5 transition-colors"
                data-testid="calc-soft-capture-btn"
              >
                Start free →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
